/**
 * showdownMoves.batch8 — batch 8 of the per-move Showdown animation ports.
 *
 * Each entry is a near-verbatim port of the move's script from Pokémon Showdown's
 * `BattleMoveAnims` (`battle-animations-moves.ts`, MIT/CC0). Our {@link FxScene} +
 * {@link Actor} mirror Showdown's `scene` + `Sprite`, so the literal
 * `scene.showEffect(...)` / `attacker.anim(...)` / `scene.wait(...)` sequences port
 * unchanged. Where a move pointed at a shared `BattleOtherAnims` template, the
 * template is either reused from {@link module:showdownOther} (when exported there)
 * or ported locally below. Several entries are Showdown aliases
 * (`BattleMoveAnims[x] = { anim: BattleMoveAnims[y].anim }`); the alias target is
 * ported inline and re-pointed, matching the existing pattern in showdownMoves.ts.
 *
 * Effect-name discipline: only effect keys vendored in our fx catalog are used.
 * Showdown's sprite-as-effect calls (`showEffect(attacker.sp, …)` /
 * `showEffect(defender.sp, …)`) have no analogue in our engine (no sprite-as-effect),
 * so they are omitted — noted inline at each site; the actor `anim` choreography that
 * accompanies them is preserved verbatim, which carries the read. Showdown's optional
 * 5th `{ filter }` arg to showEffect (CSS filters) is also dropped (unsupported).
 *
 * Test/runtime separation: this module imports ONLY TYPES from `showdownFx.ts`
 * (that file pulls in Phaser, which can't load in the node test env). Every script
 * is a pure function over the `FxScene`/`Actor` interfaces.
 */
import type { FxScene, Actor, MoveAnim } from './showdownFx.js';
import { contactattack, bite as biteTemplate, fastattack } from './showdownOther.js';

/* ──────────────────────────────────────────────────────────────────────────
 * Local template ports (PS `BattleOtherAnims` entries not exported by showdownOther).
 * ──────────────────────────────────────────────────────────────────────── */

/** PS `BattleOtherAnims.flight` — fly off-screen, swoop back through the defender. */
const flight: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  attacker.anim(
    { x: attacker.leftof(-200), y: attacker.y + 80, z: attacker.z, opacity: 0, time: 350 },
    'accel',
  );
  attacker.anim({ x: defender.leftof(-200), y: defender.y + 80, z: defender.z, time: 1 }, 'linear');
  attacker.anim({ x: defender.x, y: defender.y, z: defender.z, opacity: 1, time: 350 }, 'accel');
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 700 },
    { scale: 2, opacity: 0, time: 900 },
    'linear',
  );
  attacker.anim({
    x: defender.leftof(100),
    y: defender.y - 40,
    z: defender.z,
    opacity: 0,
    time: 175,
  });
  attacker.anim({ x: attacker.x, y: attacker.y + 40, z: attacker.behind(40), time: 1 });
  attacker.anim({ x: attacker.x, y: attacker.y, z: attacker.z, time: 250 }, 'decel');
  defender.delay(700);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `BattleOtherAnims.dance` — a small left/right sway of the user. */
const dance: MoveAnim = (_scene: FxScene, [attacker]: Actor[]) => {
  attacker.anim({ x: attacker.x - 10 });
  attacker.anim({ x: attacker.x + 10 });
  attacker.anim({ x: attacker.x });
};

/** PS `BattleOtherAnims.shake` — a slower left/right shake of the user. */
const shake: MoveAnim = (_scene: FxScene, [attacker]: Actor[]) => {
  attacker.anim({ x: attacker.x - 10, time: 200 });
  attacker.anim({ x: attacker.x + 10, time: 300 });
  attacker.anim({ x: attacker.x, time: 200 });
};

/** PS `BattleOtherAnims.selfstatus` — two wisps collapse inward on the user. */
const selfstatus: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 2, opacity: 0.2, time: 0 },
    { scale: 0, opacity: 1, time: 300 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 2, opacity: 0.2, time: 200 },
    { scale: 0, opacity: 1, time: 500 },
    'linear',
  );
};

/** PS `BattleOtherAnims.sound` — three wisps ring out behind the user. */
const sound: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.7, time: 0 },
    { z: attacker.behind(-50), scale: 5, opacity: 0, time: 400 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.7, time: 150 },
    { z: attacker.behind(-50), scale: 5, opacity: 0, time: 600 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.7, time: 300 },
    { z: attacker.behind(-50), scale: 5, opacity: 0, time: 800 },
    'linear',
  );
};

/** PS `BattleOtherAnims.anger` — three "angry" marks pop up over the user. */
const anger: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'angry',
    { x: attacker.x + 20, y: attacker.y + 20, z: attacker.z, scale: 0.5, opacity: 0.5, time: 0 },
    { scale: 1, opacity: 1, time: 300 },
    'ballistic2Under',
    'fade',
  );
  scene.showEffect(
    'angry',
    { x: attacker.x - 20, y: attacker.y + 10, z: attacker.z, scale: 0.5, opacity: 0.5, time: 100 },
    { scale: 1, opacity: 1, time: 400 },
    'ballistic2Under',
    'fade',
  );
  scene.showEffect(
    'angry',
    { x: attacker.x, y: attacker.y + 40, z: attacker.z, scale: 0.5, opacity: 0.5, time: 200 },
    { scale: 1, opacity: 1, time: 500 },
    'ballistic2Under',
    'fade',
  );
};

/** PS `BattleOtherAnims.hydroshot` — three water wisps spray onto the defender. */
const hydroshot: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.3 },
    { x: defender.x + 10, y: defender.y + 5, z: defender.behind(30), scale: 1, opacity: 0.6 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.3, time: 75 },
    { x: defender.x - 10, y: defender.y - 5, z: defender.behind(30), scale: 1, opacity: 0.6 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.3, time: 150 },
    { x: defender.x, y: defender.y + 5, z: defender.behind(30), scale: 1, opacity: 0.6 },
    'decel',
    'explode',
  );
};

/* ──────────────────────────────────────────────────────────────────────────
 * Direct move scripts.
 * ──────────────────────────────────────────────────────────────────────── */

/** PS `spark` — an electroball flares over the defender, then a contact lunge. */
const spark: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  scene.showEffect(
    'electroball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 450 },
    { x: defender.x, y: defender.y - 40, z: defender.behind(15), scale: 3, opacity: 0, time: 800 },
    'linear',
  );
  contactattack(scene, actors);
};

/** PS `spikecannon` — two electroball pellets fire onto the defender. */
const spikecannon: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.6 },
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.6, time: 300 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.2, opacity: 0.6, time: 30 },
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.6, time: 330 },
    'linear',
    'fade',
  );
};

/** PS `spikes` — three caltrops are tossed onto the ground before the defender. */
const spikes: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'caltrop',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0.5 },
    { x: -25, y: defender.y - 40, z: defender.z, scale: 0.3, opacity: 1 },
    'ballistic',
  );
  scene.showEffect(
    'caltrop',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0.5, time: 125 },
    { x: +50, y: defender.y - 40, z: defender.z, scale: 0.3, opacity: 1 },
    'ballistic',
  );
  scene.showEffect(
    'caltrop',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0.5, time: 250 },
    { x: +30, y: defender.y - 45, z: defender.z, scale: 0.3, opacity: 1 },
    'ballistic',
  );
};

/** PS `spikyshield` → `selfstatus` template. */
const spikyshield: MoveAnim = selfstatus;

/**
 * PS `spite` — a dark wash and a poison wisp drift to the defender. PS then drew
 * two ghosting copies of the defender's sprite (`showEffect(defender.sp, …)`),
 * which our engine has no analogue for; those two calls are omitted.
 */
const spite: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.backgroundEffect('#000000', 1300, 0.3);
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, opacity: 1, scale: 1 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 1.2, opacity: 0, time: 400 },
    'decel',
  );
  // PS drew two defender-sprite ghosts here (showEffect(defender.sp, …)); omitted
  // (no sprite-as-effect in our engine).
};

/** PS `splash` — water puffs flick off the user as it hops in place. */
const splash: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'waterwisp',
    { x: attacker.x + 20, y: attacker.y + 20, z: attacker.z, scale: 0.5, opacity: 0.1, time: 150 },
    { x: attacker.x + 40, y: attacker.y + 60, z: attacker.z, opacity: 0.3 },
    'ballistic',
    'fade',
  );
  scene.showEffect(
    'waterwisp',
    { x: attacker.x - 20, y: attacker.y + 20, z: attacker.z, scale: 0.5, opacity: 0.1, time: 300 },
    { x: attacker.x - 40, y: attacker.y + 60, z: attacker.z, opacity: 0.3 },
    'ballistic',
    'fade',
  );
  scene.showEffect(
    'waterwisp',
    { x: attacker.x + 20, y: attacker.y + 40, z: attacker.z, scale: 0.5, opacity: 0.1, time: 450 },
    { x: attacker.x + 60, y: attacker.y + 40, z: attacker.z, opacity: 0.3 },
    'ballistic',
    'fade',
  );
  attacker.anim({ y: attacker.y + 15, time: 150 }, 'decel');
  attacker.anim({ time: 150 }, 'accel');
  attacker.anim({ y: attacker.y + 15, time: 150 }, 'decel');
  attacker.anim({ time: 150 }, 'accel');
  attacker.anim({ y: attacker.y + 15, time: 150 }, 'decel');
  attacker.anim({ time: 150 }, 'accel');
};

/** PS `spore` — three spores drift down over the defender. */
const spore: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  scene.showEffect(
    'poisonwisp',
    { x: defender.x + 10, y: defender.y + 90, z: defender.z, opacity: 0, scale: 0.4 },
    { y: defender.y - 5, opacity: 1, time: 500 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x + 30, y: defender.y + 90, z: defender.z, opacity: 0, scale: 0.4, time: 150 },
    { y: defender.y - 5, opacity: 1, time: 650 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x - 30, y: defender.y + 90, z: defender.z, opacity: 0, scale: 0.4, time: 300 },
    { y: defender.y - 5, opacity: 1, time: 800 },
    'decel',
    'fade',
  );
};

/** PS `spotlight` — the user sways while a pointer sweeps over the defender. */
const spotlight: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  dance(scene, [attacker]);
  scene.showEffect(
    'pointer',
    { x: attacker.x + 50, y: attacker.y + 30, z: attacker.z, scale: 0.4, opacity: 1 },
    {
      x: attacker.x + 30,
      y: attacker.y + 35,
      scale: 0.5,
      xscale: 0.3,
      yscale: 0.6,
      opacity: 1,
      time: 400,
    },
    'decel',
    'fade',
  );
  scene.showEffect(
    'pointer',
    {
      x: attacker.x + 30,
      y: attacker.y + 35,
      z: attacker.z,
      scale: 0.5,
      xscale: 0.3,
      yscale: 0.6,
      opacity: 1,
      time: 400,
    },
    {
      x: attacker.x + 60,
      y: attacker.y + 30,
      scale: 0.4,
      xscale: 0.4,
      yscale: 0.4,
      opacity: 0,
      time: 900,
    },
    'linear',
  );
  defender.delay(400);
  defender.anim({ y: defender.y + 15, time: 150 }, 'decel');
  defender.anim({ time: 150 }, 'accel');
  defender.anim({ y: defender.y + 15, time: 150 }, 'decel');
  defender.anim({ time: 150 }, 'accel');
};

/** PS `stealthrock` — four rocks are flung to hover around the defender. */
const stealthrock: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'rock1',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0.5 },
    { x: defender.leftof(-40), y: defender.y - 10, z: defender.z, scale: 0.2, opacity: 1 },
    'ballistic',
  );
  scene.showEffect(
    'rock2',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0.5, time: 75 },
    { x: defender.leftof(-20), y: defender.y - 40, z: defender.z, scale: 0.2, opacity: 1 },
    'ballistic',
  );
  scene.showEffect(
    'rock1',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0.5, time: 150 },
    { x: defender.leftof(30), y: defender.y - 20, z: defender.z, scale: 0.2, opacity: 1 },
    'ballistic',
  );
  scene.showEffect(
    'rock2',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0.5, time: 225 },
    { x: defender.leftof(10), y: defender.y - 30, z: defender.z, scale: 0.2, opacity: 1 },
    'ballistic',
  );
};

/** PS `steamroller` → `contactattack` template. */
const steamroller: MoveAnim = contactattack;

/** PS `steelroller` → `steamroller` (→ `contactattack`). */
const steelroller: MoveAnim = contactattack;

/** PS `steelwing` → `flight` template. */
const steelwing: MoveAnim = flight;

/** PS `stickyweb` — a web is slung across to the defender's side. */
const stickyweb: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'web',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0.5 },
    { x: 0, y: defender.y, z: defender.z, scale: 0.5, opacity: 1 },
    'ballistic',
  );
};

/** PS `stockpile` → `selfstatus` template. */
const stockpile: MoveAnim = selfstatus;

/** PS `stuffcheeks` → `stockpile` (→ `selfstatus`). */
const stuffcheeks: MoveAnim = selfstatus;

/** PS `swallow` → `selfstatus` template. */
const swallow: MoveAnim = selfstatus;

/** PS `stomp` — a giant foot stamps down on the defender, who flattens. */
const stomp: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'foot',
    {
      x: defender.x,
      y: defender.y + 80,
      z: defender.behind(-15),
      scale: 1.5,
      opacity: 0.8,
      time: 400,
    },
    { y: defender.y - 10, z: defender.z, scale: 1, opacity: 1, time: 500 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y - 30, z: defender.z, scale: 1, time: 500 },
    { x: defender.x + 70, scale: 0.8, opacity: 0.3, time: 800 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y - 30, z: defender.z, scale: 1, time: 500 },
    { x: defender.x - 70, scale: 0.8, opacity: 0.3, time: 800 },
    'linear',
    'fade',
  );
  attacker.anim(
    { x: defender.x, y: defender.y + 80, z: defender.behind(-30), time: 400 },
    'ballistic',
  );
  attacker.anim({ x: defender.x, y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim({ time: 600 }, 'ballistic2Back');
  defender.delay(450);
  defender.anim({ y: defender.y - 30, z: defender.behind(20), yscale: 0.5, time: 200 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/**
 * PS `stompingtantrum` — anger marks, the user stamps, a ring of orbs flares, and
 * rocks burst by the defender. PS's optional `{ filter }` 5th arg to showEffect is
 * dropped (CSS filters unsupported), and the two attacker-sprite ghost calls
 * (`showEffect(attacker.sp, …)`) are omitted; the lunge below preserves the read.
 */
const stompingtantrum: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  anger(scene, [attacker, defender]);

  attacker.anim({ y: attacker.y + 20, time: 175 }, 'swing');
  attacker.anim({ y: attacker.y, time: 125 }, 'accel');
  attacker.anim({ y: attacker.y + 40, time: 175 }, 'swing');
  attacker.anim({ y: attacker.y, time: 75 }, 'accel');

  const xf = [1, -1, 1, -1];
  const yf = [1, -1, -1, 1];
  const xf2 = [1, 0, -1, 0];
  const yf2 = [0, 1, 0, -1];

  scene.backgroundEffect('#000000', 1000, 0.3);
  for (let i = 0; i < 4; i++) {
    scene.showEffect(
      'flareball',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.3, opacity: 0.8, time: 500 },
      {
        x: attacker.x + 120 * xf[i],
        y: attacker.y,
        z: attacker.z + 68 * yf[i],
        scale: 0.5,
        opacity: 0,
        time: 800,
      },
      'decel',
      'fade',
    );
    scene.showEffect(
      'flareball',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.3, opacity: 0.8, time: 500 },
      {
        x: attacker.x + 113 * xf2[i],
        y: attacker.y + 5,
        z: attacker.z + 97 * yf2[i],
        scale: 0.5,
        opacity: 0,
        time: 800,
      },
      'decel',
      'fade',
    );
  }

  defender.delay(500);

  scene.showEffect(
    'rock1',
    { x: defender.x, y: defender.y - 5, z: defender.z, scale: 0.2, opacity: 1, time: 800 },
    { x: defender.x, y: defender.y + 20, z: defender.z, scale: 0.4, opacity: 0, time: 1100 },
    'ballistic',
    'fade',
  );
  scene.showEffect(
    'rock2',
    { x: defender.x + 20, y: defender.y - 5, z: defender.z, scale: 0.2, opacity: 1, time: 800 },
    { x: defender.x + 80, y: defender.y + 20, z: defender.z, scale: 0.4, opacity: 0, time: 1100 },
    'ballistic',
    'fade',
  );
  scene.showEffect(
    'rock3',
    { x: defender.x - 20, y: defender.y - 5, z: defender.z, scale: 0.2, opacity: 1, time: 800 },
    { x: defender.x - 80, y: defender.y + 20, z: defender.z, scale: 0.4, opacity: 0, time: 1100 },
    'ballistic',
    'fade',
  );

  // PS drew two attacker-sprite ghosts here (showEffect(attacker.sp, …)); omitted.
  attacker.anim({ x: defender.x, y: defender.y, z: defender.behind(-5), time: 300 }, 'accel');
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(280);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `stoneedge` — five jagged rocks erupt upward through the defender. */
const stoneedge: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  scene.showEffect(
    'rock3',
    { x: defender.x + 15, y: defender.y - 50, z: defender.z, scale: 0.5 },
    { y: defender.y + 60, yscale: 1, opacity: 0, time: 350 },
    'accel',
  );
  scene.showEffect(
    'rock3',
    { x: defender.x + 30, y: defender.y - 50, z: defender.z, scale: 0.5, time: 150 },
    { y: defender.y + 60, yscale: 1, opacity: 0, time: 500 },
    'accel',
  );
  scene.showEffect(
    'rock3',
    { x: defender.x - 30, y: defender.y - 50, z: defender.z, scale: 0.5, time: 300 },
    { y: defender.y + 60, yscale: 1, opacity: 0, time: 650 },
    'accel',
  );
  scene.showEffect(
    'rock3',
    { x: defender.x, y: defender.y - 50, z: defender.z, scale: 0.5, time: 400 },
    { y: defender.y + 60, yscale: 1, opacity: 0, time: 750 },
    'accel',
  );
  scene.showEffect(
    'rock3',
    { x: defender.x - 15, y: defender.y - 50, z: defender.z, scale: 0.5, time: 500 },
    { y: defender.y + 60, yscale: 1, opacity: 0, time: 850 },
    'accel',
  );
};

/** PS `storedpower` — eight psychic wisps spiral outward from the user. */
const storedpower: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  const xf = [1, -1, 1, -1];
  const yf = [1, -1, -1, 1];
  const xf2 = [1, 0, -1, 0];
  const yf2 = [0, 1, 0, -1];

  for (let i = 0; i < 4; i++) {
    scene.showEffect(
      'poisonwisp',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 1 },
      {
        x: attacker.x + 240 * xf[i],
        y: attacker.y,
        z: attacker.z + 137 * yf[i],
        scale: 1,
        opacity: 0.5,
        time: 800,
      },
      'accel',
    );
    scene.showEffect(
      'poisonwisp',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 1 },
      {
        x: attacker.x + 339 * xf2[i],
        y: attacker.y,
        z: attacker.z + 194 * yf2[i],
        scale: 1,
        opacity: 0.5,
        time: 800,
      },
      'accel',
    );
  }
};

/** PS `strength` → `contactattack` template. */
const strength: MoveAnim = contactattack;

/** PS `substitute` — Showdown plays nothing (the doll swap is handled elsewhere). */
const substitute: MoveAnim = () => {};

/** PS `suckerpunch` — the fast-attack dash plus a sharp impact ring on the defender. */
const suckerpunch: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  fastattack(scene, actors);
  scene.showEffect(
    'impact',
    { x: defender.x, y: defender.y, z: defender.behind(5), scale: 0, opacity: 0.3, time: 260 },
    { scale: 1.25, opacity: 0, time: 500 },
    'linear',
  );
};

/** PS `sunnyday` → `dance` template. */
const sunnyday: MoveAnim = dance;

/** PS `superfang` — the jaw-snap effect plus a contact lunge. */
const superfang: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  biteTemplate(scene, actors);
  contactattack(scene, actors);
};

/**
 * PS `supercellslam` — the user dives onto the defender amid electric bursts. PS
 * led with two attacker-sprite ghosts diving in (`showEffect(attacker.sp, …)`);
 * those are omitted (no sprite-as-effect), the lunge below preserves the dive.
 */
const supercellslam: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  // PS drew two attacker-sprite dive ghosts here (showEffect(attacker.sp, …)); omitted.
  scene.showEffect(
    'electroball',
    { x: defender.x, y: defender.y - 40, z: defender.z, scale: 1, opacity: 1, time: 500 },
    { x: defender.x, y: defender.y + 10, z: defender.z, scale: 1.25, opacity: 0.4, time: 700 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'electroball',
    { x: defender.x - 40, y: defender.y - 40, z: defender.z, scale: 1, opacity: 1, time: 600 },
    { x: defender.x - 30, y: defender.y, z: defender.z, scale: 1.25, opacity: 0.4, time: 800 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'electroball',
    { x: defender.x + 40, y: defender.y - 40, z: defender.z, scale: 1, opacity: 1, time: 700 },
    { x: defender.x + 40, y: defender.y, z: defender.z, scale: 1.25, opacity: 0.4, time: 900 },
    'decel',
    'fade',
  );
  attacker.anim(
    { x: defender.x, y: defender.y + 170, z: defender.behind(-30), time: 400 },
    'ballistic',
  );
  attacker.anim({ x: defender.x, y: defender.y + 5, z: defender.z, time: 200 });
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(500);
  defender.anim({ y: defender.y - 30, z: defender.behind(20), yscale: 0.5, time: 200 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `supersonic` — three electroball rings ripple out to the defender. */
const supersonic: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.5, time: 0 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 0, time: 200 },
    'linear',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.5, time: 150 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 0, time: 350 },
    'linear',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.5, time: 300 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 0, time: 500 },
    'linear',
  );
};

/** PS `swagger` — the user shakes while angry marks pop over the defender. */
const swagger: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  shake(scene, [attacker]);
  scene.showEffect(
    'angry',
    { x: defender.x + 20, y: defender.y + 20, z: defender.z, scale: 0.5, opacity: 0.5, time: 0 },
    { scale: 1, opacity: 1, time: 300 },
    'ballistic2Under',
    'fade',
  );
  scene.showEffect(
    'angry',
    { x: defender.x - 20, y: defender.y + 10, z: defender.z, scale: 0.5, opacity: 0.5, time: 100 },
    { scale: 1, opacity: 1, time: 400 },
    'ballistic2Under',
    'fade',
  );
  scene.showEffect(
    'angry',
    { x: defender.x, y: defender.y + 40, z: defender.z, scale: 0.5, opacity: 0.5, time: 200 },
    { scale: 1, opacity: 1, time: 500 },
    'ballistic2Under',
    'fade',
  );
};

/** PS `swift` — a fan of six star-wisps streams onto the defender and bursts. */
const swift: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.6 },
    { x: defender.x + 30, y: defender.y + 30, z: defender.z, scale: 1, opacity: 0.3, time: 200 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.6, time: 75 },
    { x: defender.x + 20, y: defender.y - 30, z: defender.z, scale: 1, opacity: 0.3, time: 275 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.6, time: 150 },
    { x: defender.x - 30, y: defender.y, z: defender.z, scale: 1, opacity: 0.3, time: 350 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.6, time: 225 },
    { x: defender.x - 10, y: defender.y + 10, z: defender.z, scale: 1, opacity: 0.3, time: 425 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.6, time: 300 },
    { x: defender.x + 10, y: defender.y - 10, z: defender.z, scale: 1, opacity: 0.3, time: 500 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.6, time: 375 },
    { x: defender.x - 20, y: defender.y, z: defender.z, scale: 1, opacity: 0.3, time: 575 },
    'linear',
    'explode',
  );
};

/** PS `switcheroo` — three pokéballs juggle back and forth between the combatants. */
const switcheroo: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'pokeball',
    { x: attacker.x, y: attacker.y, z: attacker.z, time: 0 },
    { x: defender.x, y: defender.y - 30, z: defender.z, time: 300 },
    'ballistic2',
    'fade',
  );
  scene.showEffect(
    'pokeball',
    { x: defender.x, y: defender.y, z: defender.z, time: 0 },
    { x: attacker.x, y: attacker.y - 30, z: attacker.z, time: 300 },
    'ballistic2Under',
    'fade',
  );
  scene.showEffect(
    'pokeball',
    { x: defender.x, y: defender.y - 30, z: defender.z, time: 300 },
    { x: attacker.x - 50, y: attacker.y, z: attacker.z, time: 600 },
    'ballistic2',
    'fade',
  );
  scene.showEffect(
    'pokeball',
    { x: attacker.x, y: attacker.y - 30, z: attacker.z, time: 300 },
    { x: defender.x - 50, y: defender.y, z: defender.z, time: 600 },
    'ballistic2Under',
    'fade',
  );
  scene.showEffect(
    'pokeball',
    { x: attacker.x - 50, y: attacker.y, z: attacker.z, time: 600 },
    { x: defender.x, y: defender.y, z: defender.z, time: 900 },
    'ballistic2',
    'explode',
  );
  scene.showEffect(
    'pokeball',
    { x: defender.x - 50, y: defender.y, z: defender.z, time: 600 },
    { x: attacker.x, y: attacker.y, z: attacker.z, time: 900 },
    'ballistic2Under',
    'explode',
  );
};

/** PS `swordsdance` — the defender shakes while swords sweep across it. */
const swordsdance: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  shake(scene, [defender]);
  scene.showEffect(
    'sword',
    { x: defender.x + 50, y: defender.y, z: defender.z, scale: 0.5, opacity: 1 },
    { x: defender.x - 50, scale: 1, opacity: 0.4, time: 200 },
    'ballistic2',
    'fade',
  );
  scene.showEffect(
    'sword',
    { x: defender.x - 50, y: defender.y, z: defender.z, scale: 0.5, opacity: 1 },
    { x: defender.x + 50, scale: 1, opacity: 0.4, time: 200 },
    'ballistic2back',
    'fade',
  );
  scene.showEffect(
    'sword',
    { x: defender.x + 50, y: defender.y, z: defender.z, scale: 0.5, opacity: 1, time: 200 },
    { x: defender.x - 50, scale: 1, opacity: 0.4, time: 400 },
    'ballistic2',
    'fade',
  );
  scene.showEffect(
    'sword',
    { x: defender.x - 50, y: defender.y, z: defender.z, scale: 0.5, opacity: 1, time: 200 },
    { x: defender.x + 50, scale: 1, opacity: 0.4, time: 400 },
    'ballistic2back',
    'fade',
  );
  scene.showEffect(
    'sword',
    { x: defender.x + 50, y: defender.y, z: defender.z, scale: 0.5, opacity: 1, time: 400 },
    { x: defender.x - 50, scale: 1, opacity: 0.4, time: 600 },
    'ballistic2',
    'fade',
  );
  scene.showEffect(
    'sword',
    { x: defender.x - 50, y: defender.y, z: defender.z, scale: 0.5, opacity: 1, time: 400 },
    { x: defender.x + 50, scale: 1, opacity: 0.4, time: 600 },
    'ballistic2Back',
    'fade',
  );
};

/** PS `synchronoise` → `sound` template. */
const synchronoise: MoveAnim = sound;

/** PS `tailglow` — a bright electroball flares and bursts on the user. */
const tailglow: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 2, opacity: 0.2, time: 0 },
    { scale: 1, opacity: 0.5, time: 400 },
    'linear',
    'explode',
  );
};

/** PS `tailslap` → `contactattack` template. */
const tailslap: MoveAnim = contactattack;

/** PS `tailwhip` → `dance` template. */
const tailwhip: MoveAnim = dance;

/** PS `tailwind` — three icy gusts rush past the user from front to back. */
const tailwind: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.behind(40), scale: 0.3, opacity: 1 },
    { z: attacker.behind(-40), scale: 2, opacity: 0.2, time: 300 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.behind(40), scale: 0.3, opacity: 1, time: 200 },
    { z: attacker.behind(-40), scale: 2, opacity: 0.2, time: 500 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.behind(40), scale: 0.3, opacity: 1, time: 400 },
    { z: attacker.behind(-40), scale: 2, opacity: 0.2, time: 700 },
    'linear',
    'fade',
  );
};

/** PS `taunt` — the user sways and points; angry marks pop over the defender. */
const taunt: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  dance(scene, [attacker, defender]);
  scene.showEffect(
    'pointer',
    { x: attacker.x + 50, y: attacker.y + 30, z: attacker.z, scale: 0.4, opacity: 1 },
    {
      x: attacker.x + 30,
      y: attacker.y + 35,
      scale: 0.5,
      xscale: 0.3,
      yscale: 0.6,
      opacity: 1,
      time: 400,
    },
    'decel',
    'fade',
  );
  scene.showEffect(
    'pointer',
    {
      x: attacker.x + 30,
      y: attacker.y + 35,
      z: attacker.z,
      scale: 0.5,
      xscale: 0.3,
      yscale: 0.6,
      opacity: 1,
      time: 400,
    },
    {
      x: attacker.x + 60,
      y: attacker.y + 30,
      scale: 0.4,
      xscale: 0.4,
      yscale: 0.4,
      opacity: 0,
      time: 900,
    },
    'linear',
  );
  scene.showEffect(
    'angry',
    { x: defender.x + 20, y: defender.y + 20, z: defender.z, scale: 0.5, opacity: 0.5, time: 200 },
    { scale: 1, opacity: 1, time: 500 },
    'ballistic2Under',
    'fade',
  );
  scene.showEffect(
    'angry',
    { x: defender.x - 20, y: defender.y + 10, z: defender.z, scale: 0.5, opacity: 0.5, time: 300 },
    { scale: 1, opacity: 1, time: 600 },
    'ballistic2Under',
    'fade',
  );
  scene.showEffect(
    'angry',
    { x: defender.x, y: defender.y + 40, z: defender.z, scale: 0.5, opacity: 0.5, time: 400 },
    { scale: 1, opacity: 1, time: 700 },
    'ballistic2Under',
    'fade',
  );
};

/** PS `tearfullook` → `dance` template. */
const tearfullook: MoveAnim = dance;

/** PS `teeterdance` → `shake` template. */
const teeterdance: MoveAnim = shake;

/** PS `teleport` — the user squishes, stretches skyward, and blinks out and back. */
const teleport: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.backgroundEffect('#000000', 1000, 0.3);
  attacker.anim({ xscale: 0.3, time: 200 }, 'linear');
  attacker.anim({ y: attacker.y + 200, xscale: 0.1, yscale: 2, opacity: 0.5, time: 300 }, 'accel');
  attacker.delay(500);
  attacker.anim({ opacity: 0, time: 0 });
  attacker.anim({ opacity: 1, time: 300 });
};

/**
 * PS `temperflare` — anger marks, the user stamps, a fiery ring + smoke flares, and
 * fireballs burst by the defender. PS used a `linear-gradient(...)` backgroundEffect
 * (gradients unsupported here) — substituted with a solid deep-red wash; the optional
 * `{ filter }` 5th showEffect arg is dropped (CSS filters unsupported).
 */
const temperflare: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  anger(scene, [attacker, defender]);

  attacker.anim({ y: attacker.y + 20, time: 175 }, 'swing');
  attacker.anim({ y: attacker.y, time: 125 }, 'accel');
  attacker.anim({ y: attacker.y + 40, time: 175 }, 'swing');
  attacker.anim({ y: attacker.y, time: 75 }, 'accel');
  defender.delay(500);

  const xf = [1, -1, 1, -1];
  const yf = [1, -1, -1, 1];
  const xf2 = [1, 0, -1, 0];
  const yf2 = [0, 1, 0, -1];

  // PS used a linear-gradient backgroundEffect; substitute a solid deep-red wash.
  scene.backgroundEffect('#390000', 1000, 0.3);
  for (let i = 0; i < 4; i++) {
    scene.showEffect(
      'flareball',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.3, opacity: 0.8, time: 500 },
      {
        x: attacker.x + 120 * xf[i],
        y: attacker.y,
        z: attacker.z + 68 * yf[i],
        scale: 0.5,
        opacity: 0,
        time: 800,
      },
      'decel',
      'fade',
    );
    scene.showEffect(
      'flareball',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.3, opacity: 0.8, time: 500 },
      {
        x: attacker.x + 113 * xf2[i],
        y: attacker.y + 5,
        z: attacker.z + 97 * yf2[i],
        scale: 0.5,
        opacity: 0,
        time: 800,
      },
      'decel',
      'fade',
    );
    scene.showEffect(
      'blackwisp',
      {
        x: attacker.x + 120 * xf[i],
        y: attacker.y,
        z: attacker.z + 68 * yf[i],
        scale: 0.5,
        opacity: 0.5,
        time: 650,
      },
      {
        x: attacker.x + 120 * xf[i],
        y: attacker.y + 30,
        z: attacker.z + 68 * yf[i],
        scale: 0.5,
        opacity: 0,
        time: 950,
      },
      'decel',
      'explode',
    );
  }

  scene.showEffect(
    'fireball',
    { x: defender.x + 20, y: defender.y - 5, z: defender.z, scale: 0.4, opacity: 1, time: 800 },
    { x: defender.x + 80, y: defender.y + 20, z: defender.z, scale: 0.8, opacity: 0, time: 1100 },
    'ballistic',
    'fade',
  );
  scene.showEffect(
    'fireball',
    { x: defender.x, y: defender.y - 5, z: defender.z, scale: 0.4, opacity: 1, time: 800 },
    { x: defender.x, y: defender.y + 20, z: defender.z, scale: 0.8, opacity: 0, time: 1100 },
    'ballistic',
    'fade',
  );
  scene.showEffect(
    'fireball',
    { x: defender.x - 20, y: defender.y - 5, z: defender.z, scale: 0.4, opacity: 1, time: 800 },
    { x: defender.x - 80, y: defender.y + 20, z: defender.z, scale: 0.8, opacity: 0, time: 1100 },
    'ballistic',
    'fade',
  );
  attacker.anim({ x: defender.x, y: defender.y, z: defender.behind(-5), time: 300 }, 'accel');
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(280);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/* ──────────────────────────────────────────────────────────────────────────
 * Aliases — PS `BattleMoveAnims[x] = { anim: BattleMoveAnims[y].anim }`. Each
 * alias target is ported inline (mirroring the existing showdownMoves.ts pattern).
 * ──────────────────────────────────────────────────────────────────────── */

/** PS `bubblebeam` (target of `sparklingaria`) — four icy bubbles arc + pop. */
const bubblebeam: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.7 },
    { x: defender.x, y: defender.y, z: defender.behind(0), opacity: 0.6, time: 400 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.7, time: 100 },
    { x: defender.x + 20, y: defender.y - 10, z: defender.behind(0), opacity: 0.6, time: 500 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.7, time: 200 },
    { x: defender.x - 20, y: defender.y + 10, z: defender.behind(0), opacity: 0.6, time: 600 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.7, time: 300 },
    { x: defender.x, y: defender.y - 5, z: defender.behind(0), opacity: 0.6, time: 700 },
    'decel',
    'explode',
  );
};

/** PS `sparklingaria` → `bubblebeam`. */
const sparklingaria: MoveAnim = bubblebeam;

/** PS `skillswap` (target of `speedswap`) — wisps trade places between combatants. */
const skillswap: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y - 30, z: attacker.z, scale: 1, opacity: 1, time: 0 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.5, opacity: 0, time: 400 },
    'ballistic2Under',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y - 30, z: attacker.z, scale: 1, opacity: 1, time: 200 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.5, opacity: 0, time: 600 },
    'ballistic2Under',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 200 },
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0, time: 600 },
    'ballistic',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 400 },
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0, time: 800 },
    'ballistic',
  );
};

/** PS `speedswap` → `skillswap`. */
const speedswap: MoveAnim = skillswap;

/** PS `electroweb` (target of `spiderweb`/`stringshot`) — three webs fling onto the defender. */
const electroweb: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'web',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.5, opacity: 1, time: 400 },
    'ballistic',
    'explode',
  );
  scene.showEffect(
    'web',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0, time: 100 },
    { x: defender.x + 40, y: defender.y - 20, z: defender.z, scale: 0.5, opacity: 1, time: 500 },
    'ballistic',
    'explode',
  );
  scene.showEffect(
    'web',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0, time: 200 },
    { x: defender.x - 30, y: defender.y - 10, z: defender.z, scale: 0.5, opacity: 1, time: 600 },
    'ballistic',
    'explode',
  );
};

/** PS `spiderweb` → `electroweb`. */
const spiderweb: MoveAnim = electroweb;
/** PS `stringshot` → `electroweb`. */
const stringshot: MoveAnim = electroweb;

/** PS `magnetbomb` (target of `spitup`/`steelbeam`) — three steel orbs arc onto the defender. */
const magnetbomb: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.7, opacity: 1, time: 400 },
    'ballistic',
    'explode',
  );
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0, time: 100 },
    { x: defender.x + 40, y: defender.y - 20, z: defender.z, scale: 0.7, opacity: 1, time: 500 },
    'ballistic',
    'explode',
  );
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0, time: 200 },
    { x: defender.x - 30, y: defender.y - 10, z: defender.z, scale: 0.7, opacity: 1, time: 600 },
    'ballistic',
    'explode',
  );
};

/** PS `spitup` → `magnetbomb`. */
const spitup: MoveAnim = magnetbomb;
/** PS `steelbeam` → `magnetbomb`. */
const steelbeam: MoveAnim = magnetbomb;

/** PS `circlethrow` (target of `stormthrow`) — wisps + a chop hurl the defender. */
const circlethrow: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 400 },
    { x: defender.leftof(-20), z: defender.behind(20), scale: 3, opacity: 0, time: 700 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 500 },
    { x: defender.leftof(-20), z: defender.behind(20), scale: 0.6, opacity: 0, time: 800 },
    'linear',
  );
  scene.showEffect(
    'rightchop',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.6, opacity: 1, time: 400 },
    { x: defender.leftof(-20), z: defender.behind(20), scale: 1, opacity: 0, time: 800 },
    'linear',
  );
  attacker.anim(
    { x: defender.leftof(20), y: defender.y, z: defender.behind(-20), time: 400 },
    'ballistic2Under',
  );
  attacker.anim({ x: defender.x, y: defender.y, z: defender.z, time: 50 });
  attacker.anim({ time: 500 }, 'ballistic2');
  defender.delay(425);
  defender.anim(
    { x: defender.leftof(-15), y: defender.y, z: defender.behind(15), time: 50 },
    'swing',
  );
  defender.anim({ time: 300 }, 'swing');
};

/** PS `stormthrow` → `circlethrow`. */
const stormthrow: MoveAnim = circlethrow;

/** PS `leechlife` (target of `strengthsap`) — a contact bite that siphons life back. */
const leechlife: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.backgroundEffect('#987058', 800, 0.3, 400);
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 400 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(50),
      scale: 3,
      opacity: 0,
      time: 700,
    },
    'linear',
  );
  scene.showEffect(
    'electroball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.6, opacity: 1, time: 600 },
    { x: attacker.x, y: attacker.y, z: attacker.z, time: 900, opacity: 0 },
    'ballistic2',
  );
  scene.showEffect(
    'electroball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.6, opacity: 1, time: 650 },
    { x: attacker.x, y: attacker.y, z: attacker.z, time: 950, opacity: 0 },
    'linear',
  );
  scene.showEffect(
    'electroball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.6, opacity: 1, time: 700 },
    { x: attacker.x, y: attacker.y, z: attacker.z, time: 1000, opacity: 0 },
    'ballistic2Under',
  );
  attacker.anim(
    { x: defender.leftof(20), y: defender.y, z: defender.behind(-20), time: 400 },
    'ballistic2Under',
  );
  attacker.anim({ x: defender.x, y: defender.y, z: defender.z, time: 50 });
  attacker.anim({ time: 500 }, 'ballistic2');
  defender.delay(425);
  defender.anim(
    { x: defender.leftof(-20), y: defender.y, z: defender.behind(20), time: 50 },
    'swing',
  );
  defender.anim({ time: 300 }, 'swing');
};

/** PS `strengthsap` → `leechlife`. */
const strengthsap: MoveAnim = leechlife;

/** PS `bulletseed` (target of `strugglebug`) — two energy pellets fire onto the defender. */
const bulletseed: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'energyball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.6 },
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.6, time: 300 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'energyball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.2, opacity: 0.6, time: 30 },
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.6, time: 330 },
    'linear',
    'fade',
  );
};

/** PS `strugglebug` → `bulletseed`. */
const strugglebug: MoveAnim = bulletseed;

/** PS `closecombat` (target of `submission`/`superpower`) — a flurry of fists at point-blank. */
const closecombat: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  attacker.anim(
    { x: defender.leftof(-30), y: defender.y + 80, z: defender.behind(-30), time: 350 },
    'ballistic',
  );
  attacker.anim({ x: defender.leftof(30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim(
    { x: defender.leftof(30), y: defender.y + 80, z: defender.behind(-30), time: 150 },
    'ballisticUp',
  );
  attacker.anim({ x: defender.leftof(-30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(450);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 200 }, 'swing');
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');

  scene.showEffect(
    'fist',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.6, opacity: 0.6, time: 425 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 525 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'fist',
    { x: defender.x - 10, y: defender.y + 20, z: defender.z, scale: 0.6, opacity: 0.6, time: 450 },
    { x: defender.x - 20, y: defender.y + 30, z: defender.z, scale: 1, opacity: 1, time: 550 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'fist',
    { x: defender.x + 30, y: defender.y - 20, z: defender.z, scale: 0.6, opacity: 0.6, time: 475 },
    { x: defender.x + 35, y: defender.y - 30, z: defender.z, scale: 1, opacity: 1, time: 575 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'fist',
    { x: defender.x - 30, y: defender.y - 20, z: defender.z, scale: 0.6, opacity: 0.6, time: 575 },
    { x: defender.x - 35, y: defender.y - 30, z: defender.z, scale: 1, opacity: 1, time: 775 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'fist',
    { x: defender.x, y: defender.y - 10, z: defender.z, scale: 0.6, opacity: 0.6, time: 600 },
    { x: defender.x + 10, y: defender.y - 15, z: defender.z, scale: 1, opacity: 1, time: 750 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'fist',
    { x: defender.x, y: defender.y + 10, z: defender.z, scale: 0.6, opacity: 0.6, time: 650 },
    { x: defender.x - 10, y: defender.y + 15, z: defender.z, scale: 1, opacity: 1, time: 800 },
    'linear',
    'explode',
  );

  scene.showEffect(
    'impact',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.4, time: 525 },
    { scale: 3, opacity: 0, time: 825 },
    'linear',
  );
  scene.showEffect(
    'impact',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.4, time: 750 },
    { scale: 3, opacity: 0, time: 1050 },
    'linear',
  );
};

/** PS `submission` → `closecombat`. */
const submission: MoveAnim = closecombat;
/** PS `superpower` → `closecombat`. */
const superpower: MoveAnim = closecombat;

/** PS `slam` (target of `tackle`) → `contactattack` template. */
const tackle: MoveAnim = contactattack;

/**
 * PS `gigaimpact` (target of `takedown`) — a dark wash, twin impact rings, and a
 * full-body slam. PS led with two attacker-sprite ghosts (`showEffect(attacker.sp,…)`);
 * omitted (no sprite-as-effect) — the lunge below carries the charge read.
 */
const gigaimpact: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.backgroundEffect('#000000', 700, 0.2);
  scene.showEffect(
    'impact',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.4, time: 300 },
    { scale: 4, opacity: 0, time: 600 },
    'linear',
  );
  scene.showEffect(
    'impact',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.4, time: 500 },
    { scale: 4, opacity: 0, time: 800 },
    'linear',
  );
  // PS drew two attacker-sprite ghosts here (showEffect(attacker.sp, …)); omitted.
  attacker.anim({ x: defender.x, y: defender.y, z: defender.behind(-5), time: 300 }, 'accel');
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(280);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `takedown` → `gigaimpact`. */
const takedown: MoveAnim = gigaimpact;

/** PS `kinesis` (target of `telekinesis`) — psychic washes plus a sway. */
const kinesis: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.backgroundEffect('#AA44BB', 450, 0.6, 450);
  scene.backgroundEffect('#AA44FF', 250, 0.6, 800);
  dance(scene, [attacker]);
};

/** PS `telekinesis` → `kinesis`. */
const telekinesis: MoveAnim = kinesis;

/** PS `scald` (target of `terablast`) — the hydroshot spray plus rising steam wisps. */
const scald: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  hydroshot(scene, actors);
  scene.showEffect(
    'wisp',
    { x: defender.x + 30, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 300 },
    { y: defender.y + 60, opacity: 0.2, time: 700 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x - 30, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 400 },
    { y: defender.y + 60, opacity: 0.2, time: 800 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x + 15, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 500 },
    { y: defender.y + 60, opacity: 0.2, time: 900 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x - 15, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 600 },
    { y: defender.y + 60, opacity: 0.2, time: 1000 },
    'linear',
    'fade',
  );
};

/**
 * PS `terablast` → `scald`. (PS also has typed `terablast<type>` variants that map
 * to per-type moves; only the base `terablast` is in our roster, so we port that.)
 */
const terablast: MoveAnim = scald;

/** PS `sweetkiss` → `lovelykiss`: hearts float over and burst on the defender. PS
 * guarded the follow-up on `defender.isMissedPokemon`; our engine has no miss flag,
 * so the full sequence always plays. */
const lovelykiss: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'heart',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0 },
    {
      x: defender.leftof(40),
      y: defender.y + 15,
      z: defender.z,
      scale: 0.7,
      opacity: 0.7,
      time: 500,
    },
    'decel',
    'fade',
  );
  // PS returned early here on defender.isMissedPokemon; no miss flag in our engine.
  scene.showEffect(
    'heart',
    {
      x: defender.leftof(40),
      y: defender.y + 15,
      z: defender.z,
      scale: 0.7,
      opacity: 0.7,
      time: 500,
    },
    { x: defender.leftof(-40), y: defender.y, z: defender.z, scale: 0.6, opacity: 1, time: 700 },
    'swing',
    'fade',
  );
  scene.showEffect(
    'heart',
    { x: defender.leftof(-40), y: defender.y, z: defender.z, scale: 0.7, opacity: 0, time: 700 },
    { x: defender.leftof(10), y: defender.y - 15, z: defender.z, scale: 1, opacity: 1, time: 900 },
    'swing',
    'explode',
  );
};

/** PS `sweetkiss` → `lovelykiss`. */
const sweetkiss: MoveAnim = lovelykiss;

/** PS `mistyterrain` (target of `sweetscent`) — a pink wash plus a sway. */
const mistyterrain: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.backgroundEffect('#FF99FF', 1000, 0.3);
  dance(scene, [attacker]);
};

/** PS `sweetscent` → `mistyterrain`. */
const sweetscent: MoveAnim = mistyterrain;

/* ──────────────────────────────────────────────────────────────────────────
 * The batch-8 table, keyed by Showdown move id (toID form).
 * ──────────────────────────────────────────────────────────────────────── */

export const SHOWDOWN_BATCH_8: Record<string, MoveAnim> = {
  spark,
  sparklingaria,
  speedswap,
  spiderweb,
  spikecannon,
  spikes,
  spikyshield,
  spitup,
  spite,
  splash,
  spore,
  spotlight,
  stealthrock,
  steamroller,
  steelbeam,
  steelroller,
  steelwing,
  stickyweb,
  stockpile,
  stomp,
  stompingtantrum,
  stoneedge,
  storedpower,
  stormthrow,
  strength,
  strengthsap,
  stringshot,
  strugglebug,
  stuffcheeks,
  // stunspore → spore (PS alias)
  stunspore: spore,
  submission,
  substitute,
  suckerpunch,
  sunnyday,
  superfang,
  supercellslam,
  superpower,
  supersonic,
  swagger,
  swallow,
  sweetkiss,
  sweetscent,
  swift,
  switcheroo,
  swordsdance,
  synchronoise,
  // synthesis: PS has no `synthesis` move-anim and no alias; omitted (no anim source).
  tackle,
  tailglow,
  tailslap,
  tailwhip,
  tailwind,
  takedown,
  taunt,
  tearfullook,
  teeterdance,
  telekinesis,
  teleport,
  temperflare,
  terablast,
};
